import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../api/axiosInstance';

// A customer (signed in through the QR scan flow) has no staff token: validate / redeem use the customer token then.
const customerApi = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL });
customerApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('authx_customer_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const pickApi = () => (localStorage.getItem('authx_access_token') ? api : customerApi);
import { toast } from 'sonner';

const t = (name, fn) => createAsyncThunk(name, async (arg, { rejectWithValue }) => {
  try { return await fn(arg); }
  catch (err) {
    toast.error(err.response?.data?.message || err.message || 'Request failed');
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const fetchOfferCodes = t('offerCode/fetchAll', async () => {
  const { data } = await api.get('OfferCodes');
  return data.data;
});

export const createOfferCode = t('offerCode/create', async (dto) => {
  const { data } = await api.post('OfferCodes', dto);
  toast.success('Offer code created!');
  return data.data;
});

export const updateOfferCode = t('offerCode/update', async ({ id, dto }) => {
  const { data } = await api.put(`OfferCodes/${id}`, dto);
  toast.success('Offer code updated!');
  return data.data;
});

export const deleteOfferCode = t('offerCode/delete', async (id) => {
  await api.delete(`OfferCodes/${id}`);
  toast.success('Offer code deleted');
  return id;
});

export const setOfferCodeActive = t('offerCode/setActive', async ({ id, active }) => {
  await api.patch(`OfferCodes/${id}/status?active=${active}`);
  toast.success(active ? 'Code activated' : 'Code deactivated');
  return { id, active };
});

export const validateCode = t('offerCode/validate', async ({ code, customerId }) => {
  const params = new URLSearchParams({ code });
  if (customerId) params.append('customerId', customerId);
  const { data } = await pickApi().get(`OfferCodes/validate?${params}`);
  return data.data;
});

export const redeemCode = t('offerCode/redeem', async ({ code, customerId }) => {
  const params = new URLSearchParams({ code });
  if (customerId) params.append('customerId', customerId);
  const { data } = await pickApi().post(`OfferCodes/redeem?${params}`);
  toast.success('Code redeemed successfully!');
  return data.data;
});

const offerCodeSlice = createSlice({
  name: 'offerCode',
  initialState: {
    items: [],
    validation: null,
    loading: false,
    actionLoading: false,
    error: null,
  },
  reducers: {
    clearValidation: (s) => { s.validation = null; }
  },
  extraReducers: (b) => {
    b
      .addCase(fetchOfferCodes.pending, (s) => { s.loading = true; })
      .addCase(fetchOfferCodes.fulfilled, (s, a) => { s.loading = false; s.items = a.payload || []; })
      .addCase(fetchOfferCodes.rejected, (s) => { s.loading = false; })
      .addCase(createOfferCode.fulfilled, (s, a) => { s.items = [a.payload, ...s.items]; })
      .addCase(updateOfferCode.fulfilled, (s, a) => {
        s.items = s.items.map(i => i.codeId === a.payload.codeId ? a.payload : i);
      })
      .addCase(deleteOfferCode.fulfilled, (s, a) => {
        s.items = s.items.filter(i => i.codeId !== a.payload);
      })
      .addCase(setOfferCodeActive.fulfilled, (s, a) => {
        s.items = s.items.map(i =>
          i.codeId === a.payload.id ? { ...i, isActive: a.payload.active } : i
        );
      })
      .addCase(validateCode.fulfilled, (s, a) => { s.validation = a.payload; })
      .addCase(redeemCode.fulfilled, (s, a) => { s.validation = a.payload; });
  }
});

export const { clearValidation } = offerCodeSlice.actions;
export default offerCodeSlice.reducer;